import type { RecordIdentityCapture, RecordIdentityReceipt } from './support-record-identity';

/** Declarative checks for operator-authored support tasks. No page scripts or network reads. */
export interface SupportCheckScope {
  selector: string;
  labelSelector: string;
  labelText: string;
}

export interface SupportCheck {
  kind: 'exists' | 'absent' | 'text' | 'text-one-of' | 'value' | 'text-ignoring-usage-count' | 'collection-text-absent';
  selector: string;
  text?: string;
  texts?: string[];
  count?: number;
  caseSensitive?: boolean;
  /**
   * Bind the check to exactly one labelled parent group. A check with a scope
   * fails when that group is missing or ambiguous; it never falls back to a
   * page-wide match, so a duplicate under another parent cannot satisfy it.
   */
  scope?: SupportCheckScope;
  collection?: {
    summarySelector: string;
    summaryFormat: 'showing-range-of-total-v1';
    unfiltered: SupportCheck[];
  };
}

/** A read-only list result witness. It proves a rendered result changed after one scoped click. */
export interface SupportResultTransition {
  rowSelector: string;
  emptySelector: string;
  emptyText: string;
  paginationSelector: string;
  countSelector: string;
  columns: Array<{label: string; headerSelector: string; cellSelector: string}>;
}

export interface SupportStep {
  version: 1 | 2 | 3 | 4;
  planId: string;
  planVersion: number;
  taskId: string;
  origin: string;
  paths: string[];
  action: 'click' | 'type' | 'navigate' | 'verify' | 'file';
  selector?: string;
  /** For a hidden native file input, the visible associated host label. */
  pickerSelector?: string;
  /** Only metadata and a digest of the user's browser-selected file leave the browser. */
  file?: {
    extensions: string[];
    maxBytes: number;
    hostBehavior: 'selection-only';
    /**
     * Authored opt-in for a host that consumes the control: the exact input that
     * received the file is removed or replaced from the document as soon as it
     * accepts the selection. Identity still binds to that same input node and its
     * exact file bytes; only the "still attached to the document" requirement is
     * relaxed, and only for this authored contract.
     */
    hostConsumesInput?: true;
  };
  text?: string;
  scope?: { selector: string; labelSelector: string; labelText: string };
  value?: string;
  destination?: string;
  before?: SupportCheck[];
  checks?: SupportCheck[];
  skipIfComplete?: boolean;
  timeoutMs?: number;
  captureRecord?: RecordIdentityCapture;
  /** Capture bounded active-dialog text atomically with a verified record receipt. */
  captureReadEvidence?: 'active-dialog-v1';
  record?: RecordIdentityReceipt;
  recordChecks?: SupportCheck[];
  captureBaseline?: { key: string; selector: string };
  baseline?: { key: string; token: string };
  recordExit?: boolean;
  commitKey?: 'Enter';
  resultTransition?: SupportResultTransition;
  /** Read-only reveal of one already verified host element before the verify receipt. */
  revealSelector?: string;
}

const normalized = (text: string) => text.replace(/\s+/g, ' ').trim();
const BASELINE_KEY = /^[a-z][a-zA-Z0-9_]{0,63}$/;
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const visible = (element: Element): element is HTMLElement =>
  element instanceof HTMLElement && element.isConnected && element.getClientRects().length > 0 &&
  getComputedStyle(element).visibility !== 'hidden' &&
  !element.closest('[inert], [aria-hidden="true"], [data-floe-widget]');

const validTextComparison = (check: SupportCheck): boolean =>
  !('caseSensitive' in check) || (check.kind === 'text' && typeof check.caseSensitive === 'boolean');

function validTextOneOfCheck(check: SupportCheck): boolean {
  return check.kind === 'text-one-of' && validSelector(check.selector) &&
    Object.keys(check).every(key => ['kind', 'selector', 'texts', 'count'].includes(key)) &&
    Array.isArray(check.texts) && check.texts.length >= 2 && check.texts.length <= 4 &&
    check.texts.every(text => typeof text === 'string' && !!normalized(text) && !text.includes('{{') && text.length <= 1000) &&
    new Set(check.texts.map(normalized)).size === check.texts.length &&
    (check.count === undefined || (Number.isInteger(check.count) && check.count >= 1 && check.count <= 20));
}

const validSelector = (selector: unknown): selector is string =>
  typeof selector === 'string' && !!selector.trim() && !selector.includes('{{');

const MAX_SUPPORT_FILE_BYTES = 25 * 1024 * 1024;

const exactKeys = (value: unknown, keys: string[]): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value) &&
  Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));

function validFileContract(value: unknown): value is NonNullable<SupportStep['file']> {
  const keys = ['extensions', 'maxBytes', 'hostBehavior', 'hostConsumesInput'];
  if (!exactKeys(value, keys) && !exactKeys(value, keys.slice(0, 3))) return false;
  const file = value as NonNullable<SupportStep['file']>;
  return Array.isArray(file.extensions) && file.extensions.length >= 1 && file.extensions.length <= 8 &&
    file.extensions.every(extension => typeof extension === 'string' && /^\.[a-z0-9]{1,10}$/.test(extension)) &&
    new Set(file.extensions).size === file.extensions.length &&
    Number.isSafeInteger(file.maxBytes) && file.maxBytes > 0 && file.maxBytes <= MAX_SUPPORT_FILE_BYTES &&
    file.hostBehavior === 'selection-only' &&
    (file.hostConsumesInput === undefined || file.hostConsumesInput === true);
}

/** Basic checks that may be bound to one labelled parent group. */
const SCOPED_CHECK_KINDS = ['exists', 'absent', 'text', 'text-ignoring-usage-count', 'value'];

/**
 * A parent scope names one visible region by an exact child label. The label may
 * still carry an unresolved `{{value}}` placeholder at validation time; the
 * server binder substitutes the session value before dispatch.
 */
function validCheckScope(scope: unknown): scope is SupportCheckScope {
  if (!exactKeys(scope, ['selector', 'labelSelector', 'labelText'])) return false;
  const candidate = scope as unknown as SupportCheckScope;
  return validSelector(candidate.selector) && validSelector(candidate.labelSelector) &&
    typeof candidate.labelText === 'string' && !!candidate.labelText.trim() && candidate.labelText.length <= 1000;
}

/**
 * A master chip renders its label with a trailing parenthesised usage count, for
 * example `Busy ( 0)` or `Busy ( 12)`. The count is display state, not identity,
 * so one trailing suffix is ignored: a duplicate is caught at any count, and the
 * count never decides whether the label is present.
 */
const USAGE_COUNT_SUFFIX = /\s*\(\s*\d+\s*\)\s*$/;
const labelWithoutUsageCount = (text: string): string => normalized(text.replace(USAGE_COUNT_SUFFIX, ''));

/**
 * Whether a rendered chip label is the expected name. The expected name is
 * never rewritten; exactly one trailing count is stripped from the observed chip
 * and nothing else. A chip rendering `Follow Up (2)` is the name `Follow Up` at
 * usage count two rather than the name `Follow Up (2)`, and a reviewed name that
 * ends in numeric parentheses is matched by a chip carrying its own count
 * (`Follow Up (2) ( 0)`).
 */
const labelMatchesExpected = (text: string, expected: string): boolean =>
  labelWithoutUsageCount(text) === normalized(expected);

export function validResultTransition(value: unknown): value is SupportResultTransition {
  if (!exactKeys(value, ['rowSelector', 'emptySelector', 'emptyText', 'paginationSelector', 'countSelector', 'columns'])) return false;
  // Exact-key validation above and string validation below establish this narrow shape.
  const transition = value as unknown as SupportResultTransition;
  if (!['rowSelector', 'emptySelector', 'emptyText', 'paginationSelector', 'countSelector'].every(key => {
    const item = (transition as unknown as Record<string, unknown>)[key];
    return typeof item === 'string' && !!item.trim() && item.length <= 1000 && !item.includes('{{');
  }) || !Array.isArray(transition.columns) || transition.columns.length !== 5 ||
      transition.columns.some(column => !column || !exactKeys(column, ['label', 'headerSelector', 'cellSelector']) ||
        ![column.label, column.headerSelector, column.cellSelector].every(item =>
          typeof item === 'string' && !!item.trim() && item.length <= 1000 && !item.includes('{{'))) ||
      (['label', 'headerSelector', 'cellSelector'] as const).some(key => {
        const canonical = (value: string) => key === 'label' ? normalized(value) : value.trim();
        return new Set(transition.columns.map(column => canonical(column[key]))).size !== transition.columns.length;
      })) return false;
  try {
    document.querySelectorAll(transition.rowSelector);
    document.querySelectorAll(transition.emptySelector);
    document.querySelectorAll(transition.paginationSelector);
    document.querySelectorAll(transition.countSelector);
    transition.columns.forEach(column => { document.querySelectorAll(column.headerSelector); document.querySelectorAll(column.cellSelector); });
    return true;
  } catch { return false; }
}

/** Visible grid rows, exact empty-state text and pagination form an opaque DOM signature. */
export function resultTransitionSignature(transition: SupportResultTransition): string | null {
  if (!validResultTransition(transition)) return null;
  const text = (selector: string) => [...document.querySelectorAll(selector)].filter(visible)
    .map(element => normalized(element.innerText)).filter(Boolean);
  const headersOk = transition.columns.every(column => {
    const found = [...document.querySelectorAll(column.headerSelector)].filter(visible);
    return found.length === 1 && normalized(found[0].innerText) === normalized(column.label);
  });
  if (!headersOk) return null;
  const rowNodes = [...document.querySelectorAll(transition.rowSelector)].filter(visible);
  const rows: Array<Record<string, string>> = [];
  for (const row of rowNodes) {
    const fields: Record<string, string> = {};
    for (const column of transition.columns) {
      const cells = [...row.querySelectorAll(column.cellSelector)].filter(visible);
      if (cells.length !== 1) return null;
      fields[column.label] = normalized(cells[0].innerText);
    }
    rows.push(fields);
  }
  // AG Grid keeps its displayed no-rows overlay beneath aria-hidden=true. This
  // witness intentionally uses layout visibility for the authored empty node,
  // while retaining the normal widget/hidden exclusions. It never searches the
  // page body, so Floe's own speech/widget text cannot satisfy an empty result.
  const renderedEmpty = (element: Element): element is HTMLElement => element instanceof HTMLElement &&
    element.isConnected && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden' &&
    !element.closest('[inert], [data-floe-widget]');
  const empty = [...document.querySelectorAll(transition.emptySelector)].filter(renderedEmpty)
    .filter(element => normalized(element.innerText) === normalized(transition.emptyText)).map(() => transition.emptyText);
  const pagination = text(transition.paginationSelector);
  const counts = text(transition.countSelector);
  const activeCount = counts.length === 1 && /^\d+$/.test(counts[0]) ? Number(counts[0]) : NaN;
  const paginationTotal = pagination.length === 1
    ? normalized(pagination[0]).match(/^Showing \d+ - \d+ of (\d+) entries$/i)?.[1] : undefined;
  // A result must be either rendered rows or one explicit empty state, never both.
  // A nonempty result has exactly one pagination witness; the observed empty
  // state has none, so requiring pagination there would reject it incorrectly.
  if (!Number.isSafeInteger(activeCount) || activeCount < 0 || activeCount > 100000 ||
      (rows.length === 0) === (empty.length === 0) || empty.length > 1 ||
      (rows.length > 0
        ? (pagination.length !== 1 || paginationTotal === undefined || Number(paginationTotal) !== activeCount || activeCount <= 0)
        : (pagination.length !== 0 || activeCount !== 0)) ||
      rows.length > 100 || rows.some(row => JSON.stringify(row).length > 4096)) return null;
  return JSON.stringify({rows, empty: empty[0] ?? '', pagination: pagination[0] ?? null, activeCount});
}

export interface SupportTransitionEvidence {
  rows: Array<Record<string, string>>; empty: string; pagination?: string | null; activeCount: number;
}

export function resultTransitionEvidenceFromSignature(signature: string): SupportTransitionEvidence | null {
  try {
    const value = JSON.parse(signature) as SupportTransitionEvidence;
    const labels = transitionLabels(value.rows);
    if (!Array.isArray(value.rows) || value.rows.length > 100 || (value.rows.length > 0 && !labels) || value.rows.some(row =>
      !isTransitionRow(row, labels ?? undefined) || JSON.stringify(row).length > 4096) ||
        typeof value.empty !== 'string' || value.empty.length > 1000 || !Number.isSafeInteger(value.activeCount) || value.activeCount < 0 || value.activeCount > 100000 ||
        (value.pagination !== undefined && value.pagination !== null && (typeof value.pagination !== 'string' || value.pagination.length > 1000)) ||
        (value.rows.length === 0) === (value.empty === '')) return null;
    return {...value, pagination: value.pagination ?? null};
  } catch { return null; }
}

function transitionLabels(rows: unknown): string[] | null {
  if (!Array.isArray(rows) || !rows.length || !isTransitionRow(rows[0])) return null;
  return Object.keys(rows[0]);
}

function isTransitionRow(value: unknown, labels?: string[]): value is Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  const keys = Object.keys(row);
  return keys.length === 5 && (!labels || keys.length === labels.length && labels.every(label => Object.prototype.hasOwnProperty.call(row, label))) &&
    keys.every(key => typeof row[key] === 'string' && key.length <= 1000 && row[key].length <= 4096);
}

export function resultTransitionEvidence(transition: SupportResultTransition): SupportTransitionEvidence | null {
  const signature = resultTransitionSignature(transition);
  return signature ? resultTransitionEvidenceFromSignature(signature) : null;
}

export function validBaselineCapture(value: unknown): value is { key: string; selector: string } {
  if (!exactKeys(value, ['key', 'selector']) || typeof value.key !== 'string' || !BASELINE_KEY.test(value.key) ||
      typeof value.selector !== 'string' || value.selector.length > 1000 || !validSelector(value.selector)) return false;
  try { document.querySelectorAll(value.selector); return true; } catch { return false; }
}

export function validBaselineReference(value: unknown): value is { key: string; token: string } {
  return exactKeys(value, ['key', 'token']) && typeof value.key === 'string' && BASELINE_KEY.test(value.key) &&
    typeof value.token === 'string' && UUID_V4.test(value.token);
}

function validBasicCheck(check: SupportCheck, allowEmptyValue = true): boolean {
  if (!check || typeof check !== 'object') return false;
  if (check.kind === 'text-one-of') return validTextOneOfCheck(check);
  const kind = check.kind as string;
  if ('scope' in check && (!SCOPED_CHECK_KINDS.includes(kind) || !validCheckScope((check as {scope?: unknown}).scope))) return false;
  return SCOPED_CHECK_KINDS.includes(kind) && !('collection' in check) && !('texts' in check) &&
    validSelector(check.selector) && validTextComparison(check) &&
    (check.count === undefined || (Number.isInteger(check.count) && check.count >= 0)) &&
    (['exists', 'absent'].includes(kind) ||
      (typeof check.text === 'string' && !check.text.includes('{{') &&
        (!!check.text || (allowEmptyValue && kind === 'value'))));
}

function isNativeValueControl(element: HTMLElement): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement;
}

/** Empty value evidence is meaningful only for a native form control when one is presently visible. */
function validVisibleEmptyValueTargets(check: SupportCheck, root: ParentNode): boolean {
  if (check.kind !== 'value' || check.text !== '') return true;
  const elements = [...root.querySelectorAll(check.selector)].filter(visible);
  return elements.every(isNativeValueControl);
}

/**
 * The one visible parent group a check is bound to, or `document` when unscoped.
 * Returns null when the scope is unresolvable so the caller fails closed instead
 * of matching somewhere else on the page.
 */
function checkScopeRoot(scope: SupportCheckScope | undefined): ParentNode | null {
  if (!scope) return document;
  const label = normalized(scope.labelText).toLowerCase();
  // A visible region does not make its label visible. The candidate label must
  // itself be rendered and its rendered text must equal the label, so a hidden
  // label, or a label whose visible text hides part of itself, cannot claim the
  // reviewed parent.
  const parents = [...document.querySelectorAll(scope.selector)].filter(visible).filter(parent =>
    [...parent.querySelectorAll(scope.labelSelector)].filter(visible).some(candidate =>
      normalized(candidate.innerText).toLowerCase() === label));
  return parents.length === 1 ? parents[0] : null;
}

function validCollectionCheck(check: SupportCheck): boolean {
  const collection = check.collection;
  return check.kind === 'collection-text-absent' && validSelector(check.selector) &&
    typeof check.text === 'string' && !!normalized(check.text) && !check.text.includes('{{') &&
    !('count' in check) && !('caseSensitive' in check) && !('scope' in check) &&
    !!collection && typeof collection === 'object' && !Array.isArray(collection) &&
    validSelector(collection.summarySelector) && collection.summaryFormat === 'showing-range-of-total-v1' &&
    Array.isArray(collection.unfiltered) && collection.unfiltered.length > 0 && collection.unfiltered.length <= 20 &&
    collection.unfiltered.every(filter => validBasicCheck(filter, true) &&
      !('scope' in filter) &&
      (filter.kind !== 'text' || !!normalized(filter.text!))) &&
    collection.unfiltered.some(filter => filter.kind !== 'absent' && (filter.count ?? 1) > 0);
}

/**
 * Where a support step may START.
 *
 * `paths` is a start-page whitelist: the authored screens a step may act on. A
 * NAVIGATE step is the one exception, because moving between screens is its whole
 * job — the authorized tenant may already be anywhere on that origin (the Dashboard,
 * Manage Data, a record page) when the plan begins, and requiring the plan to have
 * predicted that page is what made entry-page coverage a plan-authoring problem.
 *
 * Only the start page is relaxed. The destination is still the authored one, and the
 * executor refuses off-origin, non-http(s) and record-mismatched destinations before
 * it navigates. Every click, fill and verification keeps the exact start-page
 * requirement, so a plan still cannot act on a screen it never declared.
 */
export function supportStartPageAllowsCurrentPage(step: SupportStep | undefined): boolean {
  if (!step || !Array.isArray(step.paths) || step.paths.length === 0) return false;
  return step.action === 'navigate' || step.paths.includes(location.pathname);
}

export function supportPageMatches(step: SupportStep): boolean {
  const fileRulesValid = step?.version === 4
    ? step.action === 'file' && validFileContract(step.file) && validSelector(step.selector) &&
      (step.pickerSelector === undefined || validSelector(step.pickerSelector)) &&
      Array.isArray(step.checks) && step.checks.length > 0
    : step?.file === undefined && step?.pickerSelector === undefined;
  const revealRulesValid = step?.revealSelector === undefined ||
    (step.version === 1 && step.action === 'verify' && validSelector(step.revealSelector) &&
      (() => { try { document.querySelectorAll(step.revealSelector!); return true; } catch { return false; } })());
  const baselineFieldsValid = (step?.captureBaseline === undefined || validBaselineCapture(step.captureBaseline)) &&
    (step?.baseline === undefined || validBaselineReference(step.baseline));
  const baselineRulesValid = baselineFieldsValid &&
    (step?.captureBaseline === undefined || (step.version === 3 && step.action === 'verify' && !!step.record &&
      Array.isArray(step.recordChecks) && !step.captureRecord && step.baseline === undefined)) &&
    (step?.baseline === undefined || (step.version === 3 && ['verify', 'click', 'type'].includes(step.action) &&
      !!step.record && !step.captureRecord && step.captureBaseline === undefined));
  const transitionRulesValid = step?.resultTransition === undefined ||
    (step.version === 3 && step.action === 'click' && validResultTransition(step.resultTransition));
  const recordReadEvidenceRulesValid = step?.captureReadEvidence === undefined ||
    (step.version === 3 && step.action === 'verify' && step.captureReadEvidence === 'active-dialog-v1' && !!step.captureRecord);
  return (step?.version === 1 || step?.version === 3 || (step?.version === 2 && ['navigate', 'click'].includes(step.action)) || (step?.version === 4 && step.action === 'file')) && typeof step.planId === 'string' && !!step.planId &&
    fileRulesValid &&
    baselineRulesValid &&
    transitionRulesValid &&
    recordReadEvidenceRulesValid &&
    revealRulesValid &&
    (step.version === 3 || !['captureRecord', 'record', 'recordChecks', 'recordExit', 'commitKey'].some(key => key in step)) &&
    Number.isInteger(step.planVersion) && step.planVersion > 0 && !!step.taskId &&
    step.origin === location.origin && supportStartPageAllowsCurrentPage(step) &&
    ['click', 'type', 'navigate', 'verify', 'file'].includes(step.action) &&
    [step.before, step.checks].every(checks => checks === undefined ||
      (Array.isArray(checks) && checks.every(check => check && typeof check === 'object' &&
        (check.kind === 'collection-text-absent'
          ? step.version === 2 && validCollectionCheck(check) : !('collection' in check)) &&
        (!('scope' in check) || validCheckScope((check as {scope?: unknown}).scope)) &&
        (!('caseSensitive' in check) || (step.version >= 2 && validTextComparison(check))))));
}

/** Absence needs all rows and the authored unfiltered scope, not a partial grid. */
function collectionTextAbsent(check: SupportCheck, elements: HTMLElement[]): boolean {
  if (!validCollectionCheck(check)) return false;
  const collection = check.collection!;
  if (!collection.unfiltered.every(filter => matches(filter, true))) return false;
  const summaries = [...document.querySelectorAll(collection.summarySelector)].filter(visible);
  if (summaries.length !== 1) return false;
  const range = normalized(summaries[0].innerText).match(/^Showing (\d+) - (\d+) of (\d+) entries$/i);
  if (!range) return false;
  const [start, end, total] = range.slice(1).map(Number);
  if (![start, end, total].every(Number.isSafeInteger) || elements.length !== total) return false;
  if (total === 0 ? start !== 0 || end !== 0 : start !== 1 || end !== total) return false;
  const expected = normalized(check.text!).toLowerCase();
  return elements.every(element => {
    const value = normalized(element.innerText).toLowerCase();
    return !!value && value !== expected;
  });
}

function matches(check: SupportCheck, allowEmptyValue = true): boolean {
  if (!check || typeof check !== 'object') return false;
  if (check.kind !== 'collection-text-absent' && !validBasicCheck(check, allowEmptyValue)) return false;
  if (check.kind === 'collection-text-absent' && !validCollectionCheck(check)) return false;
  // An unresolvable scope fails the check rather than widening to the page.
  const root = checkScopeRoot(check.scope);
  if (!root) return false;
  const elements = [...root.querySelectorAll(check.selector)].filter(visible);
  if (check.kind === 'collection-text-absent') return collectionTextAbsent(check, elements);
  const count = check.count ?? 1;
  if (check.kind === 'absent') return elements.length === 0;
  if (check.kind === 'exists') return elements.length === count;
  if (check.kind === 'text') {
    const compare = (text: string) => check.caseSensitive === false ? normalized(text).toLowerCase() : normalized(text);
    return elements.filter(el => compare(el.innerText) === compare(check.text!)).length === count;
  }
  if (check.kind === 'text-ignoring-usage-count') {
    const expected = normalized(check.text!);
    return elements.filter(el => labelMatchesExpected(el.innerText, expected)).length === count;
  }
  if (check.kind === 'text-one-of') {
    const allowed = new Set(check.texts!.map(normalized));
    return elements.filter(el => allowed.has(normalized(el.innerText))).length === count;
  }
  if (check.kind === 'value') {
    return elements.length === count && elements.every(el =>
      (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) &&
      el.value === check.text);
  }
  return false;
}

export function supportChecksPass(checks: SupportCheck[] | undefined, required = false): boolean {
  if (checks === undefined) return !required;
  if (!Array.isArray(checks) || (required && checks.length === 0) || checks.length > 20) return false;
  try { return checks.every(check => matches(check)); } catch { return false; }
}

/**
 * Validate postconditions before a conditional click can change the page.
 *
 * `navigation` is the one relaxed caller. A NAVIGATE step is validated while the
 * session is still on its source page, so a scoped destination check has no
 * rendered parent group to resolve yet. Structure, selector syntax and scope
 * shape stay strict there. Scoped checks defer rendered scope and empty-value
 * target validation to post-navigation `supportChecksPass`, which fails closed
 * when the scope stays missing or ambiguous. Checked clicks, typed Enter commits
 * and unscoped checks keep the strict form, so an unresolved current-page scope
 * can never pass those pre-action guards.
 */
export function supportChecksValid(checks: SupportCheck[] | undefined, mode: 'current-page' | 'navigation' = 'current-page'): boolean {
  if (!Array.isArray(checks) || !checks.length || checks.length > 20) return false;
  try {
    return checks.every(check => {
      if (!check || typeof check !== 'object') return false;
      if (check.kind === 'collection-text-absent') {
        if (!validCollectionCheck(check)) return false;
        document.querySelectorAll(check.collection!.summarySelector);
        for (const filter of check.collection!.unfiltered) document.querySelectorAll(filter.selector);
      } else if (mode === 'navigation' && check.scope !== undefined) {
        if (!validBasicCheck(check)) return false;
      } else {
        const root = checkScopeRoot(check.scope);
        if (!root || !validBasicCheck(check) || !validVisibleEmptyValueTargets(check, root)) return false;
      }
      document.querySelectorAll(check.selector);
      return true;
    });
  } catch { return false; }
}

/** Exactly one visible, connected host element for a verify-only read reveal. */
export function resolveSupportReveal(step: SupportStep): HTMLElement | null {
  try {
    if (!supportPageMatches(step) || step.version !== 1 || step.action !== 'verify' || !step.revealSelector) return null;
    const elements = [...document.querySelectorAll(step.revealSelector)].filter(visible);
    return elements.length === 1 ? elements[0] : null;
  } catch { return null; }
}

/** Exactly one visible element, optionally inside exactly one labelled parent. */
export function resolveSupportTarget(step: SupportStep): HTMLElement | null {
  try {
    if (!supportPageMatches(step) || !step.selector || step.selector.includes('{{')) return null;
    // A step scope resolves through the same rendered-label rule as a check
    // scope, so an unresolvable parent fails closed instead of matching a
    // same-labelled control somewhere else on the page.
    const root = checkScopeRoot(step.scope);
    if (!root) return null;
    let elements = [...root.querySelectorAll(step.selector)].filter(visible);
    if (step.text !== undefined) elements = elements.filter(el => normalized(el.innerText) === normalized(step.text!));
    if (elements.length !== 1) return null;
    const element = elements[0];
    if (element.matches(':disabled') || element.closest('[aria-disabled="true"]')) return null;
    return element;
  } catch { return null; }
}

/** Exactly one enabled native file input, reachable from a visible host control. */
export function resolveSupportFileTarget(step: SupportStep): HTMLInputElement | null {
  try {
    if (!supportPageMatches(step) || step.version !== 4 || step.action !== 'file' || !step.selector) return null;
    const candidates = [...document.querySelectorAll(step.selector)]
      .filter(element => !element.closest('[data-floe-widget]'));
    if (candidates.length !== 1 || !(candidates[0] instanceof HTMLInputElement)) return null;
    const input = candidates[0];
    if (!input.isConnected || input.type !== 'file' || input.multiple || input.matches(':disabled') ||
        input.closest('[inert], [aria-disabled="true"]')) return null;
    if (step.pickerSelector) {
      const pickers = [...document.querySelectorAll(step.pickerSelector)].filter(visible);
      if (pickers.length !== 1) return null;
      const picker = pickers[0];
      if (picker.matches(':disabled') || picker.closest('[aria-disabled="true"]') ||
          !(picker === input && visible(input) || picker instanceof HTMLLabelElement && picker.control === input)) return null;
    } else if (!visible(input)) return null;
    return input;
  } catch { return null; }
}

/** After selection, the host may hide or disable its control while processing. */
export function supportFileTargetStillBound(step: SupportStep, input: HTMLInputElement): boolean {
  try {
    if (!supportPageMatches(step) || step.version !== 4 || step.action !== 'file' || !step.selector ||
        input.type !== 'file' || input.multiple) return false;
    // An authored hostConsumesInput contract may detach this exact node once it
    // accepted the file. The node itself, its file list and its digest are still
    // the evidence; the selector is never re-resolved onto a replacement.
    if (!input.isConnected) {
      return step.file?.hostConsumesInput === true;
    }
    const candidates = [...document.querySelectorAll(step.selector)]
      .filter(element => !element.closest('[data-floe-widget]'));
    return candidates.length === 1 && candidates[0] === input;
  } catch { return false; }
}
